import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { LABELS } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { PairView } from "@/components/pair";
import { AccountPanel } from "@/components/forms";
import { Auto, Dash, ErrorNote, InlineEdit, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { money } from "@/lib/format";
import { useAccountGroups, useConnectedCount, useRefreshDirectory } from "@/lib/queries";
import type { Account, AccountGroup } from "@/lib/types";

const NO_CYCLE = "Nessun ciclo attivo";

/**
 * Quanti conti sono connessi in questo momento.
 *
 * Un conto connesso a mano resta connesso finche' qualcuno non lo stacca, e il
 * gateway si paga sui conti connessi in contemporanea: questo numero e' un
 * costo che scorre, non una statistica. La disconnessione automatica e' un'altra
 * cosa; senza questo numero non si potrebbe nemmeno scegliere una soglia.
 */
function ConnessiOra() {
  const q = useConnectedCount();
  const c = q.data;
  if (!c) return null;
  return (
    <p className="px-6 pt-4 text-sm text-muted">
      <span className="font-medium text-ink">{c.connected}</span> {c.connected === 1 ? "conto connesso" : "conti connessi"} adesso
      {c.connected > 0 && (
        <>
          {" · "}
          <span className="text-prop">{c.byRole.PROP} prop</span>
          {", "}
          <span className="text-broker">{c.byRole.BROKER} broker</span>
          {" · "}
          {c.withOpenPairs} con una gamba a mercato
          {c.alwaysOn > 0 && ` · ${c.alwaysOn} sempre accesi`}
        </>
      )}
      <span className="ml-2 text-xs text-faint">
        Restano connessi finché non li stacchi: il gateway si paga sui conti connessi in contemporanea.
      </span>
    </p>
  );
}

export function Accounts() {
  const canWrite = useCanWrite();
  const groups = useAccountGroups();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{
    group: AccountGroup;
    account: Account | null;
    role: "PROP" | "BROKER";
  } | null>(null);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <PageHeader title="Conti" subtitle="Coppie prop e broker raggruppate per cliente" />

      <ConnessiOra />

      {groups.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : groups.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(groups.error)}</ErrorNote>
        </div>
      ) : !groups.data?.length ? (
        <EmptyState
          title="Nessun conto collegato"
          hint="Aggiungi un cliente e poi i suoi due conti MT5: uno prop e uno broker. Le coppie compariranno qui con saldo e stato di connessione."
          action={
            <Link to="/clienti" className="inline-flex rounded bg-ink px-3 py-1.5 text-sm font-medium text-page hover:bg-ink/90">
              Vai ai clienti
            </Link>
          }
        />
      ) : (
        <div className="overflow-x-auto px-6 py-4">
          <p className="mb-3 text-xs text-faint">
            <span className="italic text-muted">Corsivo</span>: rilevato dal gateway. Tondo: impostato a mano. Fase, firm e taglia vengono dal ciclo attivo.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="w-8 py-2" />
                <th className="py-2 pr-4 font-medium">Cliente</th>
                <th className="py-2 pr-4 font-medium">Stato</th>
                <th className="py-2 pr-4 font-medium">Fase</th>
                <th className="py-2 pr-4 text-right font-medium">Coppie aperte</th>
                <th className="py-2 pr-4 text-right font-medium">Operazioni eseguite</th>
                <th className="py-2 pr-4 text-right font-medium text-prop">Saldo prop</th>
                <th className="py-2 pr-4 text-right font-medium text-broker">Saldo broker</th>
                <th className="py-2 pr-4 font-medium">Note</th>
                <th className="py-2 pr-4 font-medium">Firm</th>
                <th className="py-2 text-right font-medium">Taglia</th>
              </tr>
            </thead>
            <tbody>
              {groups.data.map((g) => {
                const open = expanded.has(g.client.id);
                return (
                  <Fragment key={g.client.id}>
                    <GroupRow group={g} open={open} onToggle={() => toggle(g.client.id)} canWrite={canWrite} />
                    {open && (
                      <tr className="border-b border-line">
                        <td />
                        <td colSpan={10} className="pb-4 pt-1">
                          <PairView
                            prop={g.pair.prop}
                            broker={g.pair.broker}
                            canWrite={canWrite}
                            compact
                            onEdit={(account) =>
                              setEditing({ group: g, account, role: account.role === "BROKER" ? "BROKER" : "PROP" })
                            }
                            onAdd={(role) => setEditing({ group: g, account: null, role })}
                          />
                          {g.accounts.length > (g.pair.prop ? 1 : 0) + (g.pair.broker ? 1 : 0) && (
                            <p className="mt-2 text-xs text-muted">
                              Il cliente ha altri conti fuori dalla coppia.{" "}
                              <Link to={`/clienti/${g.client.id}`} className="text-ink hover:underline">
                                Vedili nel dettaglio cliente
                              </Link>
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AccountPanel
          open
          clientId={editing.group.client.id}
          clientDefaults={{
            defaultComputerId: editing.group.client.defaultComputerId,
            defaultProxy: editing.group.client.defaultProxy,
          }}
          account={editing.account}
          defaultRole={editing.role}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function GroupRow({
  group,
  open,
  onToggle,
  canWrite,
}: {
  group: AccountGroup;
  open: boolean;
  onToggle: () => void;
  canWrite: boolean;
}) {
  const refresh = useRefreshDirectory();
  const { client, cycle, pair } = group;

  return (
    <tr className={`border-line/60 hover:bg-raised/40 ${open ? "" : "border-b"}`}>
      <td className="py-2.5">
        <button
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? `Chiudi la coppia di ${client.name}` : `Mostra la coppia di ${client.name}`}
          className="rounded p-0.5 text-muted hover:bg-raised hover:text-ink"
        >
          <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />
        </button>
      </td>
      <td className="py-2.5 pr-4">
        <Link to={`/clienti/${client.id}`} className="font-medium text-ink hover:underline">
          {client.name}
        </Link>
      </td>
      <td className="py-2.5 pr-4">
        <span className="flex items-center gap-2">
          <LegDot account={pair.prop} side="PROP" />
          <LegDot account={pair.broker} side="BROKER" />
          {cycle?.session ? (
            <span className="text-ink">{LABELS.sessionState[cycle.session.state]}</span>
          ) : (
            <span className="text-muted">Nessuna fase</span>
          )}
        </span>
      </td>
      <td className="py-2.5 pr-4">{cycle ? LABELS.cyclePhase[cycle.currentPhase] : <Dash title={NO_CYCLE} />}</td>
      <td className="py-2.5 pr-4 text-right">{cycle ? cycle.openPairs : <Dash title={NO_CYCLE} />}</td>
      <td className="py-2.5 pr-4 text-right">{cycle ? cycle.executedOps : <Dash title={NO_CYCLE} />}</td>
      <td className="py-2.5 pr-4 text-right">
        <Balance account={pair.prop} />
      </td>
      <td className="py-2.5 pr-4 text-right">
        <Balance account={pair.broker} />
      </td>
      <td className="max-w-56 py-2.5 pr-4">
        <InlineEdit
          ariaLabel={`Note di ${client.name}`}
          display={
            client.notes ? <span className="line-clamp-1 text-ink">{client.notes}</span> : <span className="text-faint">{canWrite ? "aggiungi" : "—"}</span>
          }
          disabled={!canWrite}
          initial={client.notes ?? ""}
          onSave={async (v) => {
            await api.clients.update(client.id, { notes: v.trim() === "" ? null : v.trim() });
            await refresh();
          }}
        />
      </td>
      <td className="py-2.5 pr-4">{cycle ? cycle.propFirm.name : <Dash title={NO_CYCLE} />}</td>
      <td className="py-2.5 text-right">
        {cycle ? money(cycle.accountSize, pair.prop?.currency ?? "USD") : <Dash title={NO_CYCLE} />}
      </td>
    </tr>
  );
}

/** Saldo: viene dal gateway, quindi corsivo. */
function Balance({ account }: { account: Account | null }) {
  if (!account) return <Dash title="Conto non collegato" />;
  if (account.lastBalance === null) return <Dash title="Mai sincronizzato" />;
  return <Auto>{money(account.lastBalance, account.currency)}</Auto>;
}

/** Pallino di connessione col bordo del colore della gamba. */
function LegDot({ account, side }: { account: Account | null; side: "PROP" | "BROKER" }) {
  const ring = side === "PROP" ? "ring-prop" : "ring-broker";
  const name = side === "PROP" ? "Prop" : "Broker";
  const state = !account ? "non collegato" : account.isConnected ? "connesso" : account.lastConnectError ? "errore di connessione" : "disconnesso";
  const fill = !account ? "bg-transparent" : account.isConnected ? "bg-gain" : account.lastConnectError ? "bg-loss" : "bg-faint";
  return (
    <span className={`h-2.5 w-2.5 rounded-full ring-2 ring-offset-1 ring-offset-surface ${ring} ${fill}`} title={`${name} ${state}`}>
      <span className="sr-only">
        {name} {state}
      </span>
    </span>
  );
}
